'use client'

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorMessage, Field, Formik } from "formik";
import { ArrowUpRight, Mail, Phone, User, MessageCircle, Loader2 } from "lucide-react";
import { useConsultForm } from "./hooks";

interface TProps {
  className: string
}

function ConsultForm({ className }: TProps) {
  const { initialValues, validationSchema, loading, handleSubmit } = useConsultForm();

  return (
    <div className={`${className} top-8 sticky bg-secondary text-secondary-foreground rounded-2xl p-4`}>
      <div className="flex flex-col items-center justify-center gap-6 py-4">
        <Badge className="whitespace-normal text-center font-normal text-secondary-foreground text-sm leading-snug">
          <span className="text-sm px-2 py-1 rounded-full">Get More Repeat Customers For Your Shop</span>
        </Badge>
        <h3 className="text-[28px] md:text-3xl text-center font-bold my-4">Schedule a free consultation</h3>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => {
            handleSubmit(values, actions);
            actions.resetForm();
          }}
        >
          {({ handleSubmit }) => (
            <div className="flex flex-col gap-8 w-full px-2">
              <div>
                <div className="flex items-center gap-4">
                  <User className="ml-2 absolute text-stone-400" />
                  <label htmlFor="consult-name" className="sr-only">Your Name</label>
                  <Field
                    as={Input}
                    id="consult-name"
                    type="text"
                    placeholder="Your Name"
                    name="name"
                    className="!bg-transparent border-b border-t-0 border-x-0 rounded-none pl-10"
                    disabled={loading}
                  />
                </div>
                <ErrorMessage name="name" component="p" className="text-red-500 text-sm mt-2" />
              </div>

              <div>
                <div className="flex items-center gap-4">
                  <Mail className="ml-2 absolute text-stone-400" />
                  <label htmlFor="consult-email" className="sr-only">Email Address</label>
                  <Field
                    as={Input}
                    id="consult-email"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    className="!bg-transparent border-b border-t-0 border-x-0 rounded-none pl-10"
                    disabled={loading}
                  />
                </div>
                <ErrorMessage name="email" component="p" className="text-red-500 text-sm mt-2" />
              </div>

              <div>
                <div className="flex items-center gap-4">
                  <Phone className="ml-2 absolute  text-stone-400" />
                  <label htmlFor="consult-phone" className="sr-only">Phone Number</label>
                  <Field
                    as={Input}
                    id="consult-phone"
                    name="phone"
                    type="tel"
                    placeholder="Phone Number"
                    className="!bg-transparent border-b border-t-0 border-x-0 border-stone-300 rounded-none pl-10"
                    disabled={loading}
                  />
                </div>
                <ErrorMessage name="phone" component="p" className="text-red-500 text-sm mt-2" />
              </div>

              <div>
                <div className="flex items-center gap-4">
                  <MessageCircle className="ml-2 absolute  text-stone-400" />
                  <label htmlFor="consult-message" className="sr-only">Message</label>
                  <Field
                    as={Input}
                    id="consult-message"
                    name="message"
                    type="text"
                    placeholder="Message"
                    className="!bg-transparent border-b border-t-0 border-x-0 border-stone-300 rounded-none pl-10"
                    disabled={loading}
                  />
                </div>
                <ErrorMessage name="message" component="p" className="text-red-500 text-sm mt-2" />
              </div>

              <Button type="submit" onClick={() => handleSubmit()} variant="default" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : <>Schedule now<ArrowUpRight /></>}
              </Button>
            </div>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default ConsultForm;
